/**
 * Shared placement for the map legend cards.
 *
 * Legends are rendered as independent `customComponent`s from unrelated layers,
 * so no single component knows what else is on screen. This is the small bit of
 * shared state that lets each one claim a position along the bottom edge and
 * lets the others see that it is taken.
 *
 * Assignment is computed globally from priority, not from mount order: whoever
 * has the highest priority gets its first choice, the next one gets its first
 * free choice, and so on. Mount-order-wins would make the layout depend on
 * which layer happened to render first, which flips as layers toggle.
 */

import { createContext, useContext, useEffect, useSyncExternalStore } from "react";

/** Positions along the bottom edge, left to right. */
export type LegendSlot =
  | "bottom-left"
  | "bottom-left-2"
  | "bottom-left-3"
  | "bottom-left-4"
  | "bottom-left-5";

/** Every slot, left to right — the preference list for legends that will
 *  take whatever is free. */
export const ALL_LEGEND_SLOTS: LegendSlot[] = [
  "bottom-left",
  "bottom-left-2",
  "bottom-left-3",
  "bottom-left-4",
  "bottom-left-5",
];

export const LEGEND_WIDTH = 228;

/**
 * How legend cards are laid out. "row": full cards side by side along the
 * bottom-left (the project page). "stacked": one-line chips stacked up the
 * bottom-right corner that expand on hover — for the listing's half-width
 * map pane, where five full cards would cover most of it.
 */
export type LegendLayout = "row" | "stacked";
export const LegendLayoutContext = createContext<LegendLayout>("row");
export const useLegendLayout = (): LegendLayout => useContext(LegendLayoutContext);

/** Vertical pitch of one collapsed chip in the stacked layout. */
export const STACKED_LEGEND_STEP = 34;

/**
 * Height of the hover/copyright bar at the foot of the map (Map.tsx).
 *
 * That bar is in normal flow at the bottom of the map container, but legends
 * are absolutely positioned against the same container — so their `bottom`
 * is measured from below the bar and has to clear it explicitly. Map.tsx pins
 * the bar to this height so the two cannot drift apart; without the pin the
 * height depends on the body2 line-height and any font change silently puts
 * the legends back on top of the bar.
 */
export const MAP_STATUS_BAR_HEIGHT = 24;

// The project page also pins its own FooterStatusBar to the viewport bottom,
// over the map's bar; the extra clearance keeps the cards above both.
const LEGEND_BOTTOM = MAP_STATUS_BAR_HEIGHT + 28;

/** Position of a legend's chip in the stacked layout, by its slot rank. */
export const stackedLegendStyle = (slot: LegendSlot): { bottom: number; right: number } => ({
  bottom: LEGEND_BOTTOM + Math.max(0, ALL_LEGEND_SLOTS.indexOf(slot)) * STACKED_LEGEND_STEP,
  right: 12,
});

export const LEGEND_SLOT_STYLES: Record<
  LegendSlot,
  { bottom: number; left: number }
> = {
  "bottom-left": { bottom: LEGEND_BOTTOM, left: 20 },
  "bottom-left-2": { bottom: LEGEND_BOTTOM, left: 268 },
  "bottom-left-3": { bottom: LEGEND_BOTTOM, left: 516 },
  "bottom-left-4": { bottom: LEGEND_BOTTOM, left: 764 },
  "bottom-left-5": { bottom: LEGEND_BOTTOM, left: 1012 },
};

/**
 * Higher wins the contested slot. The loaded simulation's Risk From card
 * outranks the realtime legends so it keeps the corner next to the
 * Simulations bar; lightning outranks hotspots because the far-left corner
 * is specified for lightning. (The urban legend sits mid-left, outside this
 * row.)
 */
export const LEGEND_PRIORITY = {
  riskFrom: 28,
  incidents: 25,
  lightning: 20,
  hotspot: 10,
  hazard: 9,
  ignition: 8,
  burnt_area: 7,
} as const;

export interface LegendRegistration {
  id: string;
  preferences: LegendSlot[];
  priority: number;
}

/**
 * Resolve every registered legend to a slot.
 *
 * Greedy in priority order. A legend whose preferences are all taken falls
 * back to its last preference and overlaps — visible beats vanished.
 */
export const assignLegendSlots = (
  registrations: LegendRegistration[]
): Map<string, LegendSlot> => {
  const ordered = [...registrations].sort(
    (a, b) => b.priority - a.priority || a.id.localeCompare(b.id)
  );
  const taken = new Set<LegendSlot>();
  const result = new Map<string, LegendSlot>();
  for (const reg of ordered) {
    const slot =
      reg.preferences.find((s) => !taken.has(s)) ??
      reg.preferences[reg.preferences.length - 1];
    taken.add(slot);
    result.set(reg.id, slot);
  }
  return result;
};

const registry = new Map<string, LegendRegistration>();
const listeners = new Set<() => void>();

// Version counter rather than a snapshot object: useSyncExternalStore needs a
// stable reference between changes, and a fresh object each call would loop.
let version = 0;
const getSnapshot = () => version;
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const emit = () => {
  version += 1;
  listeners.forEach((l) => l());
};

/** Test seam — the registry is module state and outlives a single render. */
export const __resetLegendSlots = () => {
  registry.clear();
  emit();
};

/**
 * Claim a slot for this legend. Returns the slot it actually got, which may
 * not be its first choice if a higher-priority legend is already there.
 */
export const useLegendSlot = (
  id: string,
  preferences: LegendSlot[],
  priority: number
): LegendSlot => {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const preferenceKey = preferences.join(",");

  useEffect(() => {
    registry.set(id, { id, preferences, priority });
    emit();
    return () => {
      registry.delete(id);
      emit();
    };
    // preferenceKey stands in for the array, which is a fresh literal each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, preferenceKey, priority]);

  // Include self even before the effect registers it, so the very first render
  // already lands in the right place instead of flashing in the wrong slot.
  const entries = [...registry.values()].filter((r) => r.id !== id);
  entries.push({ id, preferences, priority });
  return assignLegendSlots(entries).get(id) ?? preferences[0];
};
